import { useState, useEffect, useRef } from 'react';
import {
  PlusIcon,
  SaveIcon,
  Copy,
  Check,
  SearchIcon,
  MoreVertical,
  StarIcon,
  EditIcon,
  TrashIcon,
  MonitorIcon,
  UsersIcon,
  MousePointerClickIcon,
  MailIcon,
  PhoneIcon,
  ImageIcon,
  LinkIcon,
  CalendarIcon,
  UserIcon,
  FileTextIcon,
  MousePointerIcon,
  EyeOffIcon,
  EyeIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';

import { tenantApi, CTAMetrics } from '../lib/tenant-api';
import { getErrorMessage } from '../lib/api-client';
import { PageLoader } from '../components/common';
import { StatCard } from '../components/StatCard';

interface CTA {
  id: string;
  tenant_id: string;
  name: string;
  cta_type: string;
  text: string;
  image_url?: string;
  content?: string;
  title?: string;
  description?: string;
  link?: string;
  link_type?: string;
  interactive_flow_id?: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AppointmentConfig {
  id: string;
  tenant_id: string;
  services: string[];
  is_active: boolean;
}

const CTA_TYPES: Record<string, { label: string; description: string; icon: React.ComponentType<any> }> = {
  generic: { label: 'Generic Button', description: 'Simple call-to-action button', icon: MousePointerIcon },
  link: { label: 'Link Button', description: 'Button that opens external link', icon: LinkIcon },
  image_button: { label: 'Image Button', description: 'Clickable image CTA', icon: ImageIcon },
  appointment_booking: { label: 'Book Appointment', description: 'Initialize appointment booking flow', icon: CalendarIcon },
  form_email: { label: 'Email Form', description: 'Collect email addresses', icon: MailIcon },
  form_phone: { label: 'Phone Form', description: 'Collect phone numbers', icon: PhoneIcon },
  form_name: { label: 'Name Form', description: 'Collect names', icon: UserIcon },
  form_full: { label: 'Full Details Form', description: 'Collect comprehensive information', icon: FileTextIcon },
};

function getClickLabel(ctaType: string): string {
  if (['form_email', 'form_phone', 'form_name', 'form_full'].includes(ctaType)) {
    return 'Form Clicks';
  }
  return 'Button Clicks';
}

export default function CTAManagement() {
  const [ctas, setCtas] = useState<CTA[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCTAModal, setShowCTAModal] = useState(false);
  const [editingCTA, setEditingCTA] = useState<CTA | null>(null);
  const [defaultMultiCtaTitle, setDefaultMultiCtaTitle] = useState('');
  const [appointmentConfigs, setAppointmentConfigs] = useState<AppointmentConfig[]>([]);
  const [copiedActionId, setCopiedActionId] = useState(false);

  // New state for redesign
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [ctaMetrics, setCtaMetrics] = useState<CTAMetrics | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [ctaForm, setCTAForm] = useState({
    name: '',
    cta_type: 'generic',
    text: '',
    content: '',
    title: '',
    description: '',
    link: '',
    link_type: '_self',
    image_url: '',
    interactive_flow_id: '',
    is_default: false,
  });

  useEffect(() => {
    loadData();
    loadDefaultMultiCtaTitle();
    loadMetrics();
  }, []);

  // Lazy-load appointment configs only when needed
  useEffect(() => {
    if (ctaForm.cta_type === 'appointment_booking' && appointmentConfigs.length === 0) {
      loadAppointmentConfigs();
    }
  }, [ctaForm.cta_type]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMetrics = async () => {
    try {
      const metrics = await tenantApi.getCTAMetrics();
      setCtaMetrics(metrics);
    } catch (error) {
      console.error('Failed to load CTA metrics:', error);
    }
  };

  const loadAppointmentConfigs = async () => {
    try {
      const configs = await tenantApi.getAppointmentConfigs();
      setAppointmentConfigs(configs);
    } catch (error) {
      console.error('Failed to load appointment configs:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const ctasResponse = await tenantApi.getCTAs();
      setCtas(ctasResponse);
    } catch (error) {
      console.error('Failed to load CTA data:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultMultiCtaTitle = async () => {
    try {
      const config = await tenantApi.getCTAConfig();
      setDefaultMultiCtaTitle(config.default_multi_cta_title || '');
    } catch (error) {
      console.error('Failed to load default multi-CTA title:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleSaveDefaultMultiCtaTitle = async () => {
    try {
      await tenantApi.updateCTAConfig({
        default_multi_cta_title: defaultMultiCtaTitle,
      });
      toast.success('Default multi-CTA title saved');
    } catch (error) {
      console.error('Failed to save default multi-CTA title:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleCTASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      let updatedCTA: CTA;
      if (editingCTA) {
        updatedCTA = (await tenantApi.updateCTA(editingCTA.id, ctaForm)) as CTA;
        setCtas(ctas.map((cta) => (cta.id === editingCTA.id ? updatedCTA : cta)));
      } else {
        updatedCTA = (await tenantApi.createCTA(ctaForm)) as CTA;
        setCtas([updatedCTA, ...ctas]);
      }
      setShowCTAModal(false);
      resetCTAForm();
      loadMetrics();
    } catch (error) {
      console.error('Failed to save CTA:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCTA = async (ctaId: string) => {
    if (!confirm('Are you sure you want to delete this CTA?')) return;
    try {
      await tenantApi.deleteCTA(ctaId);
      setCtas(ctas.filter((cta) => cta.id !== ctaId));
      setOpenMenuId(null);
      loadMetrics();
    } catch (error) {
      console.error('Failed to delete CTA:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleSetDefaultCTA = async (ctaId: string) => {
    try {
      await tenantApi.setDefaultCTA(ctaId);
      setCtas(ctas.map((cta) => ({ ...cta, is_default: cta.id === ctaId })));
      setOpenMenuId(null);
    } catch (error) {
      console.error('Failed to set default CTA:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleRemoveDefaultCTA = async (ctaId: string) => {
    try {
      const updatedCTA = (await tenantApi.updateCTA(ctaId, { is_default: false } as any)) as CTA;
      setCtas(ctas.map((cta) => (cta.id === ctaId ? updatedCTA : cta)));
      setOpenMenuId(null);
    } catch (error) {
      console.error('Failed to remove default CTA:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleActiveCTA = async (cta: CTA) => {
    try {
      const updatedCTA = (await tenantApi.updateCTA(cta.id, { is_active: !cta.is_active } as any)) as CTA;
      setCtas(ctas.map((c) => (c.id === cta.id ? updatedCTA : c)));
      setOpenMenuId(null);
      toast.success(updatedCTA.is_active ? 'CTA activated' : 'CTA deactivated');
    } catch (error) {
      console.error('Failed to toggle CTA status:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const openCTAModal = (cta?: CTA) => {
    if (cta) {
      setEditingCTA(cta);
      let linkType = cta.link_type || '_self';
      if (linkType === 'new') linkType = '_blank';
      if (linkType === 'current') linkType = '_self';
      setCTAForm({
        cta_type: cta.cta_type,
        name: cta.name,
        text: cta.text,
        content: cta.content || '',
        title: cta.title || '',
        description: cta.description || '',
        link: cta.link || '',
        link_type: linkType,
        image_url: typeof cta.image_url === 'string' ? cta.image_url : '',
        interactive_flow_id: cta.interactive_flow_id || '',
        is_default: cta.is_default,
      });
    } else {
      setEditingCTA(null);
      resetCTAForm();
    }
    setShowCTAModal(true);
    setOpenMenuId(null);
  };

  const resetCTAForm = () => {
    setCTAForm({
      name: '',
      cta_type: 'generic',
      text: '',
      content: '',
      title: '',
      description: '',
      link: '',
      link_type: '_self',
      image_url: '',
      interactive_flow_id: '',
      is_default: false,
    });
  };

  const getClickCount = (cta: CTA): number => {
    if (!ctaMetrics) return 0;

    if (ctaMetrics.by_cta) {
      // Check by_cta first (for non-form CTAs like generic, link, image_button, appointment_booking)
      const byCta = ctaMetrics.by_cta.find((c) => c.cta_id === cta.id);
      if (byCta) return byCta.total_clicks;
    }

    if (ctaMetrics.custom_form_clicks) {
      // Check custom_form_clicks (for form-type CTAs like form_email, form_phone, etc.)
      const formClick = ctaMetrics.custom_form_clicks.find((c) => c?.cta_id === cta.id);
      if (formClick) return formClick.total_clicks;
    }

    return 0;
  };

  // Filtered CTAs
  const filteredCtas = ctas.filter((cta) => {
    const matchesSearch =
      !searchQuery ||
      cta.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cta.text.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && cta.is_active) ||
      (statusFilter === 'inactive' && !cta.is_active);
    const matchesType = typeFilter === 'all' || cta.cta_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return <PageLoader />;
  }

  const totalClicks = ctaMetrics?.summary?.total_clicks || 0;
  const activeCtas = ctas.filter((c) => c.is_active).length;

  return (
    <div>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">CTAs</h1>
            <p className="mt-2 text-sm text-gray-600">
              Create and manage call-to-action buttons that appear with AI responses
            </p>
          </div>
          <button
            onClick={() => openCTAModal()}
            className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create CTA
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard title="Total CTA" value={ctas.length} icon={MonitorIcon} />
          <StatCard title="Active CTAs" value={activeCtas} icon={UsersIcon} />
          <StatCard title="Total CTA Clicks" value={totalClicks} icon={MousePointerClickIcon} />
        </div>

        {/* Default Multi-CTA Title */}
        <div className="bg-white shadow border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Default Multi- CTA Title</h2>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={defaultMultiCtaTitle}
              onChange={(e) => setDefaultMultiCtaTitle(e.target.value)}
              className="flex-1 border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="E.g   How can we help you? or Choose an option"
            />
            <button
              type="button"
              onClick={handleSaveDefaultMultiCtaTitle}
              className="inline-flex items-center px-3 py-2.5 border border-gray-300 text-sm font-medium bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              title="Save"
            >
              <SaveIcon className="h-5 w-5 text-gray-700" />
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            This title will appear when multiple CTAs are returned together
          </p>
        </div>

        {/* Search & Filters */}
        <div className="bg-white shadow border border-gray-200 p-6 space-y-10">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-gray-50 hover:bg-gray-100"
                placeholder="Search CTA"
              />
            </div>
            <div className="px-4 py-3 w-full sm:w-40 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 flex justify-between items-center space-x-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                className="w-full bg-transparent outline-none focus:outline-none"
              >
                <option value="all">Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="px-4 py-3 w-full sm:w-40 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 flex justify-between items-center space-x-2">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full bg-transparent outline-none focus:outline-none"
              >
                <option value="all">Type</option>
                {Object.entries(CTA_TYPES).map(([value, { label }]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>


          {/* Call to Actions heading + Grid */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Call to Actions</h2>

            {filteredCtas.length === 0 ? (
              <div className="bg-white shadow border border-gray-200 p-12 text-center">
                <p className="text-gray-500">
                  {ctas.length === 0
                    ? 'No CTAs created yet. Create your first CTA to get started.'
                    : 'No CTAs match your filters.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredCtas.map((cta) => {
                  const typeInfo = CTA_TYPES[cta.cta_type] || CTA_TYPES.generic;
                  const TypeIcon = typeInfo.icon;
                  const clicks = getClickCount(cta);

                  return (
                    <div
                      key={cta.id}
                      className="bg-white border border-gray-200 hover:shadow transition-shadow flex flex-col"
                    >
                      {/* Card Header */}
                      <div className="px-4 pt-4 pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <h3 className="text-base font-semibold text-gray-900 truncate">{cta.name}</h3>
                            {cta.is_default && (
                              <StarIcon className="h-4 w-4 text-yellow-400 fill-current flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cta.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-600'
                                }`}
                            >
                              {cta.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {/* Three-dot menu */}
                            <div className="relative" ref={openMenuId === cta.id ? menuRef : undefined}>
                              <button
                                onClick={() => setOpenMenuId(openMenuId === cta.id ? null : cta.id)}
                                className="p-1 text-gray-400 hover:text-gray-600"
                              >
                                <MoreVertical className="h-5 w-5" />
                              </button>
                              {openMenuId === cta.id && (
                                <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 shadow z-10">
                                  <button
                                    onClick={() => openCTAModal(cta)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <EditIcon className="h-4 w-4 mr-2" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => cta.is_default ? handleRemoveDefaultCTA(cta.id) : handleSetDefaultCTA(cta.id)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <StarIcon className="h-4 w-4 mr-2" />
                                    {cta.is_default ? 'Remove as default' : 'Set as default'}
                                  </button>
                                  <button
                                    onClick={() => handleToggleActiveCTA(cta)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    {cta.is_active ? (
                                      <><EyeOffIcon className="h-4 w-4 mr-2" />Deactivate</>
                                    ) : (
                                      <><EyeIcon className="h-4 w-4 mr-2" />Activate</>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteCTA(cta.id)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                  >
                                    <TrashIcon className="h-4 w-4 mr-2" />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Type badge */}
                        <div className="mt-2">
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded">
                            <TypeIcon className="h-3.5 w-3.5" />
                            {typeInfo.label}
                          </span>
                        </div>
                      </div>

                      <div className='bg-gray-50 border border-gray-200 m-4 mb-6 rounded'>
                        {/* Button Preview */}
                        <div className="px-4 pb-3 flex-1">
                          <p className="text-xs font-medium text-gray-500 mt-4">Preview</p>
                          <div className="flex items-center justify-center min-h-[80px] rounded mb-2 mt-1">
                            {cta.cta_type === 'image_button' && cta.image_url ? (
                              <img
                                src={cta.image_url}
                                alt={cta.name}
                                className="max-h-24 w-auto object-contain rounded"
                              />
                            ) : (
                              <span className="inline-flex items-center justify-center px-5 py-3 text-sm font-medium bg-indigo-700 text-white w-full rounded">
                                {cta.text || cta.name}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Description */}
                        <div className="p-4 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                          <p className="text-sm text-gray-700 line-clamp-1">{cta.description || '---'}</p>
                        </div>
                      </div>

                      {/* Footer: Click count */}
                      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-sm text-gray-500">{getClickLabel(cta.cta_type)}</span>
                        <span className="text-sm font-semibold text-gray-900">{clicks}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>


      </div>

      {/* Create/Edit CTA Modal */}
      {showCTAModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="relative mx-auto border w-full max-w-2xl shadow bg-white max-h-[95vh] sm:max-h-[90vh] flex flex-col">
            <div className="px-5 pt-5 pb-3 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {editingCTA ? 'Edit CTA' : 'Create New CTA'}
              </h3>
            </div>
            <form onSubmit={handleCTASubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    required
                    value={ctaForm.name}
                    onChange={(e) => setCTAForm({ ...ctaForm, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Internal name for this CTA"
                  />
                </div>
                {editingCTA && (
                  <div className="bg-indigo-50 border border-indigo-200 p-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Action ID (for function integration)
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        readOnly
                        value={editingCTA.id}
                        className="flex-1 block w-full border border-gray-300 px-3 py-2 bg-gray-50 text-gray-600 text-sm font-mono focus:outline-none cursor-default"
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(editingCTA.id);
                            setCopiedActionId(true);
                            setTimeout(() => setCopiedActionId(false), 2000);
                          } catch (err) {
                            console.error('Failed to copy:', err);
                          }
                        }}
                        className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 transition-colors"
                        title="Copy Action ID"
                      >
                        {copiedActionId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-gray-600">
                      Use this ID to register custom functions in your widget script
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <select
                    value={ctaForm.cta_type}
                    onChange={(e) => setCTAForm({ ...ctaForm, cta_type: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {Object.entries(CTA_TYPES).map(([value, { label }]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                {ctaForm.cta_type === 'appointment_booking' && (
                  <div className="bg-amber-50 border border-amber-200 p-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Appointment Config <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={ctaForm.interactive_flow_id}
                      onChange={(e) => setCTAForm({ ...ctaForm, interactive_flow_id: e.target.value })}
                      className="block w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                    >
                      <option value="">Select an appointment configuration</option>
                      {appointmentConfigs.map((config) => (
                        <option key={config.id} value={config.id}>
                          {config.services.length > 0
                            ? config.services.join(', ')
                            : `Config ${config.id.slice(0, 8)}`}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-gray-600">
                      This determines which appointment flow will be triggered when the button is clicked
                    </p>
                  </div>
                )}

                {ctaForm.cta_type === 'image_button' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Image URL</label>
                    <input
                      type="text"
                      required
                      value={ctaForm.image_url}
                      onChange={(e) => setCTAForm({ ...ctaForm, image_url: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Paste image URL here"
                    />
                    <div className="mt-2">
                      {ctaForm.image_url ? (
                        <img src={ctaForm.image_url} alt="Preview" className="h-16 w-auto shadow" />
                      ) : (
                        <span className="text-xs text-gray-400">No image selected</span>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="mt-2"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            const asset = await tenantApi.uploadAsset(file);
                            if (asset && asset.custom_metadata && asset.custom_metadata.public_url) {
                              setCTAForm((form) => ({
                                ...form,
                                image_url: asset.custom_metadata.public_url,
                              }));
                            }
                          } catch (err) {
                            alert('Image upload failed');
                          }
                        }
                      }}
                    />
                    <span className="text-xs text-gray-400 block mt-1">
                      Please upload a landscape image around 600×300 px
                    </span>
                    <label className="block text-sm font-medium text-gray-700 mt-4">Link (optional)</label>
                    <input
                      type="text"
                      value={ctaForm.link}
                      onChange={(e) => setCTAForm({ ...ctaForm, link: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="https://example.com or tel:+1234567890"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Button Text</label>
                    <input
                      type="text"
                      required
                      value={ctaForm.text}
                      onChange={(e) => setCTAForm({ ...ctaForm, text: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Get Quote"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Content Message</label>
                  <textarea
                    value={ctaForm.content}
                    onChange={(e) => {
                      const newContent = e.target.value;
                      if (ctaForm.title && ctaForm.title === ctaForm.content) {
                        setCTAForm({ ...ctaForm, content: newContent, title: newContent });
                      } else {
                        setCTAForm({ ...ctaForm, content: newContent });
                      }
                    }}
                    className="mt-1 block w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    rows={2}
                    placeholder="Message to display with the CTA"
                  />
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      id="sync_title"
                      checked={!!ctaForm.title && ctaForm.title === ctaForm.content}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCTAForm({ ...ctaForm, title: ctaForm.content });
                        } else {
                          setCTAForm({ ...ctaForm, title: '' });
                        }
                      }}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <label htmlFor="sync_title" className="ml-2 block text-sm text-gray-700">
                      Set as CTA title
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <input
                    type="text"
                    value={ctaForm.description}
                    onChange={(e) => setCTAForm({ ...ctaForm, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Optional description"
                  />
                </div>
                {(ctaForm.cta_type === 'generic' || ctaForm.cta_type === 'link') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Link</label>
                      <input
                        type="text"
                        value={ctaForm.link}
                        onChange={(e) => setCTAForm({ ...ctaForm, link: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="https://example.com or tel:+1234567890"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Link Type</label>
                      <select
                        value={ctaForm.link_type}
                        onChange={(e) => setCTAForm({ ...ctaForm, link_type: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="_blank">New Tab</option>
                        <option value="_self">Current Tab</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">Choose where the link should open</p>
                    </div>
                  </>
                )}
                <div className="flex items-center pb-4">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={ctaForm.is_default}
                    onChange={(e) => setCTAForm({ ...ctaForm, is_default: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <label htmlFor="is_default" className="ml-2 block text-sm text-gray-900">
                    Set as default CTA
                  </label>
                </div>
              </div>
              <div className="px-5 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setShowCTAModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingCTA ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
